import { useMutation } from '@apollo/client';
import { StaticImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import FormInfoBookingOrder from '../../../components/FormInfo/BookingOrder/FormInfo';
import { getDate } from '../../../components/FormInfo/BookingOrder/helpers';
import { CheckmarkBox, DownloadButton, QrCodeWrapper } from '../../../components/FormInfo/BookingOrder/styled';
import { CenteredInfoContainer } from '../../../components/FormInfo/styled';
import { FORM_INFO_COMPONENT_TYPES, FORM_INFO_TYPES } from '../../../components/FormInfo/types';
import Checkmark from '../../../images/checkmark.svg';
import { vars } from '../../../reactive';
import { isAndroid, isiOS, replaceVariables } from '../../../utils';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';
import client from '../../../utils/client';
import config from '../../../utils/config';
import { orderPaymentAction } from '../../../utils/handleOrderPaymentAction';
import { GetAppUserOrdersPaymentActionRequired, GetUserProfile, StartMultiOrder, StartOrder, StartRecurringAppointmentOrder, SubmitFormsAnswers } from '../../../utils/queries';

const NewBookingOrder = () => {
  const selectedProduct = vars.selectedProduct()!;
  const subsciptionType = selectedProduct?.type === 'subscription';
  const isRecurring = vars.recurrenceType() === 'recurring';
  const formsAnswers = vars.formsAnswers();
  const selectedOrderGroupSlots = vars.selectedOrderGroupSlots()!;
  const type = isRecurring ? FORM_INFO_TYPES.BOOKING_ORDER_RECURRING : subsciptionType ? FORM_INFO_TYPES.BOOKING_ORDER_SUBSCRIPTION : FORM_INFO_TYPES.BOOKING_ORDER;
  const { title, body } = FORM_INFO_COMPONENT_TYPES[type];

  const slotProduct = selectedProduct?.booking_type === 'SLOT';
  const sessions = selectedProduct?.sessions!;

  const [addBooking, { data, loading, error, called }] = useMutation(StartOrder);
  const [addRecurringBooking, { data: recurringData, loading: recurringLoading, error: recurringError, called: recurringCalled }] = useMutation(StartRecurringAppointmentOrder);
  const [addMultiBooking, { loading: multiLoading, error: multiError, called: multiCalled }] = useMutation(StartMultiOrder);

  const [pessimisticPaymentLoading, setPessimisticPaymentLoading] = useState<boolean>(false);
  const [paymentPendingError, setPaymentPendingError] = useState<string>();

  const [submitForms, { loading: loadingFormsSubmit }] = useMutation(SubmitFormsAnswers);

  const orderId: string = recurringData?.startRecurringAppointmentOrder?.id || data?.startOrder?.id;

  const submitedFromsIds = [...new Set(formsAnswers?.map(form => form.formId))];

  const waitForPaymentActionsHandling = async () => {
    setPessimisticPaymentLoading(true);
    // wait for stripe update to the order if pending
    await sleep(3000);
    const {
      data: { getAppUserOrdersPaymentActionRequired: orders }
    } = await client.query<{
      getAppUserOrdersPaymentActionRequired: Queries.CollarAPI_GetAppUserOrdersPaymentActionRequiredOutput[];
    }>({
      query: GetAppUserOrdersPaymentActionRequired,
      fetchPolicy: 'no-cache'
    });

    const { data: { getProfile: profile } = {} } = await client.query<{ getProfile: Queries.CollarAPI_AppUser }>({
      query: GetUserProfile,
      fetchPolicy: 'no-cache'
    });

    const { errors } = await orderPaymentAction(orders, profile?.payment);
    if (errors.length) {
      setPaymentPendingError(errors[0]?.message);
    }
    setPessimisticPaymentLoading(false);
  };

  const formsToSubmit = submitedFromsIds.map(formId => {
    const filteredFormAnswers = formsAnswers?.filter(form => form.formId === formId);
    return {
      FormId: formId,
      OrderId: orderId,
      answers: filteredFormAnswers.flatMap(form => {
        const questionId = form.questionId;
        const answers = filteredFormAnswers.filter(form => form.questionId === questionId).flatMap(form => form.answers);
        const answersToSubmit = answers.map(answer => ({
          ...answer,
          QuestionId: questionId
        }));
        return answersToSubmit;
      })
    };
  });
  const isMultiOrder = selectedOrderGroupSlots.length > 1;

  useEffect(() => {
    const orders = vars.newOrders();

    if (orders?.length && !loading && !recurringLoading && !multiLoading && orders?.[0]) {
      if (isMultiOrder) {
        addMultiBooking({
          variables: {
            startMultiOrderInput: {
              startMultiOrder: orders,
              submitFormsAnswersInput: {
                submitFormsAnswers: formsToSubmit
              }
            }
          }
        }).finally(async () => {
          vars.newOrders(null);
          trackEvent(AnalyticsEvents.SERVICE_BOOKED, {
            name: orders.map(order => order.items?.flatMap(item => item.name)).join(', ')
          });
          vars.formsAnswers([]);
          vars.selectedForms([]);
          await waitForPaymentActionsHandling();
        });
        return;
      }

      if (isRecurring) {
        addRecurringBooking({
          variables: {
            ...orders[0],
            submitFormsAnswersInput: {
              submitFormsAnswers: formsToSubmit
            }
          }
        }).finally(async () => {
          vars.newOrders(null);
          trackEvent(AnalyticsEvents.SERVICE_BOOKED, {
            name: selectedProduct?.name
          });
          vars.formsAnswers([]);
          vars.selectedForms([]);
          await waitForPaymentActionsHandling();
        });
        return;
      }

      addBooking({
        variables: {
          ...orders[0]
        }
      }).finally(async () => {
        vars.newOrders(null);
        trackEvent(selectedProduct?.type === 'subscription' ? AnalyticsEvents.USER_SUBSCRIBED : AnalyticsEvents.SERVICE_BOOKED, {
          name: selectedProduct?.name
        });
        await waitForPaymentActionsHandling();
      });
    }
  }, []);

  useEffect(() => {
    if (orderId && formsToSubmit?.length && !loadingFormsSubmit) {
      submitForms({ variables: { submitFormsAnswersInput: { submitFormsAnswers: formsToSubmit } } }).finally(() => {
        vars.formsAnswers([]);
        vars.selectedForms([]);
      });
    }
  }, [orderId]);

  const isDeviceiOS = isiOS();
  const isDeviceAndroid = isAndroid();
  const anyCalled = called || recurringCalled || multiCalled;
  const loadingOrder = anyCalled && loading;
  const loadingRecurringOrder = anyCalled && recurringLoading;
  const loadingMultiOrder = anyCalled && multiLoading;

  return (
    <FormInfoBookingOrder
      error={!!error || !!recurringError || !!multiError || !!paymentPendingError}
      errorMessage={error?.message || recurringError?.message || multiError?.message || paymentPendingError}
      type={type}
      loading={loadingOrder || loadingRecurringOrder || loadingMultiOrder || loadingFormsSubmit || pessimisticPaymentLoading}
      title={
        selectedProduct?.additional_info?.booking_order_close_message ??
        replaceVariables(title, {
          date: selectedOrderGroupSlots.length > 1 || sessions > 1 || !slotProduct ? selectedProduct?.name : getDate(selectedOrderGroupSlots?.[0]?.slots?.[0] || new Date()),
          subscriptionName: selectedProduct?.name
        })
      }
      body={replaceVariables(body, {
        downloadMethod: !isDeviceAndroid && !isDeviceiOS ? ' by scanning the QR code below with your phone’s camera' : ''
      })}
      aboveTitleElement={
        <CenteredInfoContainer>
          <CheckmarkBox>
            <Checkmark />
          </CheckmarkBox>
        </CenteredInfoContainer>
      }
    >
      {isDeviceiOS || isDeviceAndroid ? (
        <DownloadButton target="_blank" href={isDeviceiOS ? config.APP_IOS_URL : config.APP_ANDROID_URL}>
          Download
        </DownloadButton>
      ) : (
        <QrCodeWrapper>
          <StaticImage src="../../../images/qrCode.png" alt="Qr code" quality={100} loading="eager" formats={['webp']} />
        </QrCodeWrapper>
      )}
    </FormInfoBookingOrder>
  );
};

export default NewBookingOrder;

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
